import { useState, useCallback, useRef, type DragEvent, type ReactNode } from 'react';
import type { WidgetSize } from './widgets';
import { WIDGET_REGISTRY, getAllWidgets } from './widgets';

// ── Types ──

export interface WidgetPlacement {
  widgetId: string;
  size: WidgetSize;
}

export interface WidgetGridProps {
  defaultLayout: WidgetPlacement[];
  renderWidget: (widgetId: string, size: WidgetSize) => ReactNode;
  storageKey?: string;
}

const STORAGE_PREFIX = 'helios-dashboard-layout';

const SIZE_TO_GRID: Record<WidgetSize, { col: string; row: string }> = {
  '1x1': { col: 'span 1', row: 'span 1' },
  '2x1': { col: 'span 2', row: 'span 1' },
  '1x2': { col: 'span 1', row: 'span 2' },
  '2x2': { col: 'span 2', row: 'span 2' },
};

const SIZES: WidgetSize[] = ['1x1', '2x1', '1x2', '2x2'];

// ── Helpers ──

function loadLayout(key: string, fallback: WidgetPlacement[]): WidgetPlacement[] {
  try {
    const stored = localStorage.getItem(`${STORAGE_PREFIX}-${key}`);
    if (stored) {
      const parsed = JSON.parse(stored) as WidgetPlacement[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return fallback;
}

function saveLayout(key: string, layout: WidgetPlacement[]): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}-${key}`, JSON.stringify(layout));
  } catch {
    // Ignore quota errors
  }
}

// ── Component ──

export default function WidgetGrid({ defaultLayout, renderWidget, storageKey = 'main' }: WidgetGridProps) {
  const [layout, setLayout] = useState<WidgetPlacement[]>(() => loadLayout(storageKey, defaultLayout));
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [resizingIdx, setResizingIdx] = useState<number | null>(null);
  const dragSourceIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Persist layout changes
  const updateLayout = useCallback(
    (newLayout: WidgetPlacement[]) => {
      setLayout(newLayout);
      saveLayout(storageKey, newLayout);
    },
    [storageKey],
  );

  // ── Drag & Drop ──

  const handleDragStart = (e: DragEvent<HTMLDivElement>, index: number) => {
    dragSourceIdx.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    // Make ghost semi-transparent
    if (e.currentTarget) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: DragEvent<HTMLDivElement>) => {
    e.currentTarget.style.opacity = '1';
    dragSourceIdx.current = null;
    setDragOverIdx(null);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIdx(index);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>, targetIdx: number) => {
    e.preventDefault();
    const sourceIdx = dragSourceIdx.current;
    if (sourceIdx === null || sourceIdx === targetIdx) {
      setDragOverIdx(null);
      return;
    }

    const newLayout = [...layout];
    const [moved] = newLayout.splice(sourceIdx, 1);
    newLayout.splice(targetIdx, 0, moved);
    updateLayout(newLayout);
    setDragOverIdx(null);
  };

  // ── Resize ──

  const handleResize = (index: number) => {
    if (resizingIdx === index) {
      setResizingIdx(null);
      return;
    }
    setResizingIdx(index);
  };

  const applySize = (index: number, newSize: WidgetSize) => {
    const newLayout = [...layout];
    newLayout[index] = { ...newLayout[index], size: newSize };
    updateLayout(newLayout);
    setResizingIdx(null);
  };

  // ── Remove widget ──

  const removeWidget = (index: number) => {
    const newLayout = layout.filter((_, i) => i !== index);
    updateLayout(newLayout);
  };

  // ── Add widget ──

  const addWidget = (widgetId: string) => {
    const def = WIDGET_REGISTRY[widgetId];
    if (!def) return;
    const newLayout = [...layout, { widgetId, size: def.defaultSize }];
    updateLayout(newLayout);
    setShowAddMenu(false);
  };

  // ── Reset ──

  const resetLayout = () => {
    updateLayout(defaultLayout);
    setResizingIdx(null);
    setShowAddMenu(false);
  };

  // Available widgets (not already in layout)
  const usedIds = new Set(layout.map((w) => w.widgetId));
  const availableWidgets = getAllWidgets().filter((w) => !usedIds.has(w.id));

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative">
          <button
            onClick={() => setShowAddMenu(!showAddMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-tertiary text-gray-300 hover:text-gray-100 hover:bg-surface-tertiary/80 transition-colors border border-border-subtle"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Ajouter un widget
          </button>

          {showAddMenu && (
            <div className="absolute top-full left-0 mt-1 z-50 w-72 max-h-80 overflow-y-auto bg-surface-secondary border border-border-subtle rounded-lg shadow-xl p-2">
              {availableWidgets.length > 0 ? (
                availableWidgets.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => addWidget(w.id)}
                    className="w-full text-left px-3 py-2 rounded-md hover:bg-surface-tertiary transition-colors"
                  >
                    <p className="text-sm text-gray-200">{w.name}</p>
                    <p className="text-[10px] text-gray-500">{w.description}</p>
                  </button>
                ))
              ) : (
                <p className="text-xs text-gray-500 px-3 py-2">Tous les widgets sont affiches</p>
              )}
            </div>
          )}
        </div>

        <button
          onClick={resetLayout}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-surface-tertiary text-gray-300 hover:text-gray-100 hover:bg-surface-tertiary/80 transition-colors border border-border-subtle"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
          </svg>
          Reinitialiser
        </button>
      </div>

      {/* Click-away for add menu */}
      {showAddMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowAddMenu(false)} />
      )}

      {/* Grid */}
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: 'repeat(4, 1fr)',
          gridAutoRows: 'minmax(200px, auto)',
        }}
      >
        {layout.map((placement, index) => {
          const gridStyle = SIZE_TO_GRID[placement.size];
          const isDragOver = dragOverIdx === index;

          return (
            <div
              key={`${placement.widgetId}-${index}`}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              className={`relative group transition-all duration-200 ${
                isDragOver ? 'ring-2 ring-cyber-cyan/50 ring-offset-2 ring-offset-surface-primary rounded-lg' : ''
              }`}
              style={{
                gridColumn: gridStyle.col,
                gridRow: gridStyle.row,
              }}
            >
              {/* Widget controls overlay */}
              <div className="absolute top-1 right-1 z-10 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Resize button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResize(index);
                  }}
                  className="p-1 rounded bg-surface-secondary/90 text-gray-400 hover:text-gray-200 border border-border-subtle"
                  title="Redimensionner"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                </button>

                {/* Drag handle */}
                <div className="p-1 rounded bg-surface-secondary/90 text-gray-400 cursor-grab border border-border-subtle">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                  </svg>
                </div>

                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeWidget(index);
                  }}
                  className="p-1 rounded bg-surface-secondary/90 text-gray-400 hover:text-red-400 border border-border-subtle"
                  title="Retirer"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Resize picker */}
              {resizingIdx === index && (
                <div className="absolute top-8 right-1 z-20 bg-surface-secondary border border-border-subtle rounded-lg shadow-xl p-2 flex gap-1">
                  {SIZES.map((s) => (
                    <button
                      key={s}
                      onClick={(e) => {
                        e.stopPropagation();
                        applySize(index, s);
                      }}
                      className={`px-2 py-1 text-[10px] rounded font-mono ${
                        placement.size === s
                          ? 'bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary border border-transparent'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}

              {/* Widget content */}
              {renderWidget(placement.widgetId, placement.size)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
